// Error copy for the Igloo staff endpoints (setup + assign-doors). Both share the same three
// documented 400 causes, so the mapper lives here rather than being duplicated per endpoint.
//
// The response interceptor rejects with { ...axiosError, message: <backend message or ''> }, so
// both `response.status` and `message` are readable — the same shape
// getResendGuestInviteErrorMessage relies on.
type IglooStaffError = {
  message?: string
  response?: { status?: number }
}

// Captured live from staging 2026-07-27:
//   400 "IglooWork staff access is not enabled for building 4839"
// The other two 400 causes (ineligible role, user not assigned to the building) are gated behind
// that same check, so their literal strings could not be captured yet. The backend message is
// preferred whenever present, which keeps this correct for causes we have not seen.
const GENERIC_400 =
  'Could not update Iglooworks staff access. Check that Iglooworks staff access is enabled for this property, that the user is assigned to it, and that their role is Maintenance, Front Desk or Property Manager.'

// Takes `unknown` so callers can pass a catch variable straight through.
export const getIglooStaffErrorMessage = (error: unknown): string => {
  const { message, response } = (error ?? {}) as IglooStaffError
  const backendMessage = typeof message === 'string' ? message.trim() : ''

  switch (response?.status) {
    case 400:
      return backendMessage || GENERIC_400
    case 403:
      return "You don't have permission to manage Iglooworks access for this property."
    case 404:
      return 'User or Iglooworks integration not found for this property.'
    default:
      return backendMessage || 'Could not update Iglooworks staff access. Please try again.'
  }
}

// Plain-language copy for the per-door `reason` codes chitowntrainer-sails#6346 returns. Unmapped
// codes fall through to the door name alone rather than leaking a raw slug into a toast.
const DOOR_FAILURE_COPY: Record<string, string> = {
  duplicate_key: 'already has a key from another property',
  expiry_update_failed: 'expiration could not be updated',
  mint_failed: 'key could not be issued',
  // The eKey reached the lock but the backend could not record it. Igloo keys are not recallable,
  // so the member CAN open this door — saying "failed" without that would be actively misleading.
  mint_unconfirmed: 'key was issued but not recorded — access is active',
  revoke_failed: 'key could not be revoked — access is still active',
  unknown_device: 'not a door of this property',
}

export const getDoorFailureCopy = (reason?: string): string | undefined =>
  reason ? DOOR_FAILURE_COPY[reason] : undefined

// Names the doors a save asked for but did not get.
//
// Two backend shapes, one function. Before chitowntrainer-sails#6346 a per-door failure was
// audit-logged and the door was omitted from the response, so the only signal was the absence
// diff. #6346 returns the door with `result: 'failed'` and a `held` flag instead — which means an
// absence-only check would see a failed mint sitting in the response and call it a success.
//
// So: an explicitly-marked door is always a failure, and the absence diff still runs on top. On the
// old shape no door is ever marked and this reduces exactly to the previous behaviour.
export const getFailedDoorIds = ({
  explicitlyFailed = [],
  heldBefore,
  persisted,
  requested,
}: {
  /** Ids the backend explicitly marked `result: 'failed'`, whatever their `held` value. */
  explicitlyFailed?: string[]
  heldBefore: string[]
  /** Doors the member can still open — see getHeldDoorIds. Excludes anything marked `held: false`. */
  persisted: string[]
  requested: string[]
}): { failedApplies: string[]; failedRemoves: string[] } => {
  const persistedSet = new Set(persisted)
  const requestedSet = new Set(requested)
  const heldBeforeSet = new Set(heldBefore)
  const failedSet = new Set(explicitlyFailed)

  return {
    // Asked for and either absent from the persisted state or explicitly marked failed. Absence
    // covers BOTH a failed mint on a new door and a door that was already held, was requested to
    // stay, and vanished anyway — the second case is just as much a failure as the first, and
    // splitting them on `heldBefore` would silently drop it. The explicit check is what catches a
    // `duplicate_key`, which #6346 returns with `held: true` and would otherwise read as a success.
    failedApplies: requested.filter((id) => !persistedSet.has(id) || failedSet.has(id)),
    // Dropped from the request but still persisted, and was held before -> the revoke failed.
    failedRemoves: persisted.filter((id) => !requestedSet.has(id) && heldBeforeSet.has(id)),
  }
}
